/**
 * Centralized logging utility for the application
 * Provides controlled console output based on environment and debug settings
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  VERBOSE = 4
}

export interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  enabledModules: string[];
  disabledModules: string[];
  showTimestamp: boolean;
  showModule: boolean;
}

// Default configuration
const defaultConfig: LoggerConfig = {
  enabled: process.env.NODE_ENV === 'development',
  level: LogLevel.INFO,
  enabledModules: [],
  disabledModules: [
    'portfolio-allocation',
    'positions-table',
    'auth-context',
    'protected-route',
    'api-client',
    'websocket',
    'market-data',
    'position-metrics',
    'sector-mapping',
    'unified-positions',
    'use-account-streamer'
  ],
  showTimestamp: false,
  showModule: true
};

// Load configuration from environment or localStorage
function loadConfig(): LoggerConfig {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('logger-config');
    if (stored) {
      try {
        return { ...defaultConfig, ...JSON.parse(stored) };
      } catch {
        // Ignore parse errors
      }
    }
  }

  // Check environment variables
  const envConfig: Partial<LoggerConfig> = {};
  
  if (process.env.NEXT_PUBLIC_LOG_LEVEL) {
    envConfig.level = LogLevel[process.env.NEXT_PUBLIC_LOG_LEVEL as keyof typeof LogLevel] ?? LogLevel.INFO;
  }
  
  if (process.env.NEXT_PUBLIC_LOG_ENABLED) {
    envConfig.enabled = process.env.NEXT_PUBLIC_LOG_ENABLED === 'true';
  }

  if (process.env.NEXT_PUBLIC_LOG_MODULES) {
    envConfig.enabledModules = process.env.NEXT_PUBLIC_LOG_MODULES.split(',');
  }

  return { ...defaultConfig, ...envConfig };
}

let config = loadConfig();

// Allow runtime configuration updates
export function updateLoggerConfig(updates: Partial<LoggerConfig>) {
  config = { ...config, ...updates };
  if (typeof window !== 'undefined') {
    localStorage.setItem('logger-config', JSON.stringify(config));
  }
}

export function resetLoggerConfig() {
  config = defaultConfig;
  if (typeof window !== 'undefined') {
    localStorage.removeItem('logger-config');
  }
}

export function getLoggerConfig(): LoggerConfig {
  return { ...config };
}

// Helper to check if a module should log
function shouldLog(module: string, level: LogLevel): boolean {
  if (!config.enabled) return false;
  if (level > config.level) return false;

  // Check if module is explicitly disabled
  if (config.disabledModules.includes(module)) return false;
  
  // If enabledModules is empty, allow all (except disabled)
  // If not empty, only allow explicitly enabled modules
  if (config.enabledModules.length > 0) {
    return config.enabledModules.includes(module);
  }

  return true;
}

// Format log message
function formatMessage(module: string, message: unknown, ...args: unknown[]): unknown[] {
  const parts: string[] = [];
  
  if (config.showTimestamp) {
    parts.push(`[${new Date().toISOString()}]`);
  }
  
  if (config.showModule) {
    parts.push(`[${module}]`);
  }
  
  if (parts.length > 0) {
    return [parts.join(' '), message, ...args];
  }
  
  return [message, ...args];
}

// Create a logger instance for a specific module
export function createLogger(module: string) {
  return {
    error: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(module, LogLevel.ERROR)) {
        console.error(...formatMessage(module, message, ...args));
      }
    },
    warn: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(module, LogLevel.WARN)) {
        console.warn(...formatMessage(module, message, ...args));
      }
    },
    info: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(module, LogLevel.INFO)) {
        console.info(...formatMessage(module, message, ...args));
      }
    },
    log: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(module, LogLevel.INFO)) {
        console.log(...formatMessage(module, message, ...args));
      }
    },
    debug: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(module, LogLevel.DEBUG)) {
        console.log(...formatMessage(module, message, ...args));
      }
    },
    verbose: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(module, LogLevel.VERBOSE)) {
        console.log(...formatMessage(module, message, ...args));
      }
    }
  };
}

// Global logger control functions
export const Logger = {
  enable: () => updateLoggerConfig({ enabled: true }),
  disable: () => updateLoggerConfig({ enabled: false }),
  setLevel: (level: LogLevel) => updateLoggerConfig({ level }),
  enableModule: (module: string) => {
    const current = config.enabledModules;
    if (!current.includes(module)) {
      updateLoggerConfig({ enabledModules: [...current, module] });
    }
  },
  disableModule: (module: string) => {
    const current = config.disabledModules;
    if (!current.includes(module)) {
      updateLoggerConfig({ disabledModules: [...current, module] });
    }
  },
  enableAllModules: () => {
    updateLoggerConfig({ enabledModules: [], disabledModules: [] });
  },
  disableAllModules: () => {
    updateLoggerConfig({ enabled: false });
  },
  getConfig: getLoggerConfig,
  reset: resetLoggerConfig
};

// Export convenience methods for quick debugging
export default Logger;